import http from 'hub-http/clients/apiClient';
import { createRpcClientV2 } from 'rpc-client-utils';
import {
  deleteAccountRpc,
  listAccountsForAppRpc,
  uninstallAppRpc,
  verifyAccountRpc,
  type AccountInfo,
  type DeleteAccountRequestType,
  type DeleteAccountResponseType,
  type UninstallAppRequestType,
  type UninstallAppResponseType,
  type VerifyAccountRequestType,
  type VerifyAccountResponseType,
} from '../../../__generated__/chirp/com/hubspot/unifiedaccount/rpc/AccountRpc';
import { useListAccountsForAppQuery as useListAccountsForAppQueryGenerated } from '../../../__generated__/chirp/com/hubspot/unifiedaccount/rpc/AccountRpc.hooks';
import { useListSupportedAppsQuery as useListSupportedAppsQueryGenerated } from '../../../__generated__/chirp/com/hubspot/unifiedaccount/rpc/AppRpc.hooks';

// Import and re-export types for convenience
import type { App } from '../../../__generated__/chirp/com/hubspot/unifiedaccount/rpc/AppRpc';
import { AuthFlow } from '../../shared/components/LowLevelAuthFlowView/types';
import { AuthOrigin, OAuthParamsV2 } from '../../shared/types';
export type {
  AccountInfo,
  App,
  DeleteAccountResponseType,
  UninstallAppResponseType,
  VerifyAccountResponseType,
};

export function useListSupportedAppsQuery(options?: { timeout?: number }) {
  return useListSupportedAppsQueryGenerated({
    context: options?.timeout ? { timeout: options.timeout } : undefined,
  });
}

export function useListAccountsForAppQuery(
  appId: number,
  options?: { timeout?: number; skip?: boolean }
) {
  return useListAccountsForAppQueryGenerated({
    variables: { appId },
    context: options?.timeout ? { timeout: options.timeout } : undefined,
    skip: options?.skip,
  });
}

// Create RPC client for imperative calls
const chirpClient = createRpcClientV2({
  hubHttpClient: http,
});

/**
 * Deletes a unified account
 */
export function deleteAccount(
  accountId: number
): Promise<DeleteAccountResponseType> {
  return chirpClient.call(deleteAccountRpc, {
    request: { accountId } as DeleteAccountRequestType,
  });
}

/**
 * Lists the portal's accounts for an app. Imperative counterpart of
 * `useListAccountsForAppQuery` for callers that can't rely on a
 * DataFetchingClientProvider being mounted (e.g. intro screens).
 */
export async function listAccountsForApp(
  appId: number
): Promise<AccountInfo[]> {
  const response = await chirpClient.call(listAccountsForAppRpc, {
    request: { appId },
  });
  return response.accounts;
}

/**
 * Deletes/uninstalls an app and all its associated accounts
 */
export function uninstallApp(appId: number): Promise<UninstallAppResponseType> {
  return chirpClient.call(uninstallAppRpc, {
    request: { appId } as UninstallAppRequestType,
  });
}

/**
 * Verifies that a unified account's credentials are still working
 */
export function verifyAccount(
  accountId: number
): Promise<VerifyAccountResponseType> {
  return chirpClient.call(verifyAccountRpc, {
    request: { accountId } as VerifyAccountRequestType,
  });
}

export interface CreateAccountRequest {
  sessionId: string;
  inputParams: Record<string, string>;
  upstreamParams: Record<string, string>;
  appId: number;
  oauthParams?: {
    state?: string;
    scope?: string;
  };
  customParams?: Record<string, string>;
}

// Generic Result type for algebraic data type handling
export type Result<T, E> =
  | ({ '@result': 'OK' } & T)
  | ({ '@result': 'ERR' } & E);

// Success response type
export interface CreateAccountSuccess {
  accountId: number;
}

// Error response type
export interface UnifiedAccountError {
  chirpErrorMessage: string;
  errorType: string;
  errorCategory: string;
  errorDetails: string;
  localizedErrorMessage: string;
  englishErrorMessage: string;
  existingAccountId?: number;
  extraDetailsMessage?: string;
  upstreamError?: {
    upstreamRequestId: string;
    upstreamErrorMessageInterpretation?: string;
  };
}

// Combined response type
export type CreateAccountResponse = Result<
  CreateAccountSuccess,
  UnifiedAccountError
>;

/**
 * Creates a new unified account
 */
export function createAccount(
  portalId: number,
  createAccountRequest: CreateAccountRequest
): Promise<CreateAccountResponse> {
  return http.post('/api/unified-account/v1/account/create', {
    data: createAccountRequest,
    query: { portalId },
  });
}

export interface StartAuthFlowRequest {
  appId: number;
  appName?: string;
  origin?: AuthOrigin;
}

export interface StartAuthFlowSuccess {
  sessionId: string;
  authenticationFlow: AuthFlow;
  appName: string;
}

export type StartAuthFlowResponse = Result<
  StartAuthFlowSuccess,
  UnifiedAccountError
>;

/**
 * Creates a new unified account
 * For now the `appName` in the request is optional.
 * Response contains `appName` from the request (if it exists) or from the app listing:
 * https://github.com/HubSpotEngineering/UnifiedAccount/pull/335
 */

export function startAuthFlow(
  portalId: number,
  startAuthFlowRequest: StartAuthFlowRequest
): Promise<StartAuthFlowResponse> {
  return http.post('/api/unified-account/v1/account/start', {
    data: startAuthFlowRequest,
    query: { portalId },
  });
}

/**
 * Gets the authorize URL for a new unified account
 */

export interface GetAuthorizeUrlRequest {
  appId: number;
  sessionId: string;
  inputParams: Record<string, string>;
  oauthParams?: OAuthParamsV2;
  customParams?: Record<string, string>;
}

export interface GetAuthorizeUrlSuccess {
  authorizeUrl: string;
}

export type GetAuthorizeUrlResponse = Result<
  GetAuthorizeUrlSuccess,
  UnifiedAccountError
>;

export function getAuthorizeUrl(
  portalId: number,
  getAuthorizeUrlRequest: GetAuthorizeUrlRequest
): Promise<GetAuthorizeUrlResponse> {
  return http.post('/api/unified-account/v1/account/authorize', {
    data: getAuthorizeUrlRequest,
    query: { portalId },
  });
}

/**
 * Gets the authorize URL for a existing unified account
 */

export interface GetAuthorizeForReconnectUrlRequest {
  accountId: number;
  sessionId: string;
  inputParams: Record<string, string>;
  oauthParams?: OAuthParamsV2;
  incremental?: boolean;
  customParams?: Record<string, string>;
}

export interface GetAuthorizeUrlSuccess {
  authorizeUrl: string;
}

export type GetAuthorizeUrlForReconnectResponse = Result<
  GetAuthorizeUrlSuccess,
  UnifiedAccountError
>;

export function getAuthorizeForReconnectUrl(
  portalId: number,
  getAuthorizeForReconnectUrlRequest: GetAuthorizeForReconnectUrlRequest
): Promise<GetAuthorizeUrlForReconnectResponse> {
  return http.post('/api/unified-account/v1/account/authorize-for-reconnect', {
    data: getAuthorizeForReconnectUrlRequest,
    query: { portalId },
  });
}

export interface ReconnectAccountRequest {
  accountId: number;
  sessionId: string;
  inputParams: Record<string, string>;
  upstreamParams: Record<string, string>;
  oauthParams?: {
    state?: string;
    scope?: string;
  };
  incremental?: boolean;
  customParams?: Record<string, string>;
}

export type ReconnectAccountResponse = Result<{}, UnifiedAccountError>;

export function reconnectAccount(
  portalId: number,
  reconnectAccountRequest: ReconnectAccountRequest
): Promise<ReconnectAccountResponse> {
  return http.post('/api/unified-account/v1/account/reconnect', {
    data: reconnectAccountRequest,
    query: { portalId },
  });
}

export interface MigrationReconnectRequest {
  appId: number;
  sessionId: string;
  inputParams: Record<string, string>;
  upstreamParams: Record<string, string>;
  oauthParams?: OAuthParamsV2;
  oAuthorAccountId?: string;
  customParams?: Record<string, string>;
}

export interface MigrationReconnectSuccess {
  accountId: number;
}

export type MigrationReconnectResponse = Result<
  MigrationReconnectSuccess,
  UnifiedAccountError
>;

export function migrationReconnectAccount(
  portalId: number,
  migrationReconnectRequest: MigrationReconnectRequest
): Promise<MigrationReconnectResponse> {
  return http.post('/api/unified-account/v1/migration/oauthor/account/create', {
    data: migrationReconnectRequest,
    query: { portalId },
  });
}
